import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  ContentTemplate,
  Grid,
  MUIDataTable,
  MUIDataTableColumn,
  MUIDataTableOptions,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  useDataTable,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import { PaginatedData } from '../../../../models/List';
import { FileUploadState } from '../../../../models/File';
import { candidateService } from '../../../../api';
import { RetrieveS3URLResponse } from '../../../../api/document/files/api-services/model';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../../hooks/dropdown';
import { getIndexValue } from '../../../../utils/ArrayUtils';
import {
  ResultsAppealFunctionRequest,
  ResultsAppealRequestParam,
  ResultsAppealSearchResponse,
} from '../../../../api/candidate/appeal/model';
import RouteKey from '../../../../routes/key';
import {
  actionColumn,
  appealFeeColumn,
  applicationDateColumn,
  candidateTypeColumn,
  examLevelColumn,
  paymentModeColumn,
} from './ResultsAppealColumn';
import { formAPIPageable, parseLocationSearch } from '../../../../utils/StringUtils';
import { decryptData, encryptData } from '../../../../utils/CryptoUtils';
import { useUserDetails } from '../../../../hooks/userDetails';
import { useGetCheckAuthorityExist } from '../../../../api-refactored/user/useGetCheckAuthorityExist';

const { admin } = RouteKey.child;
const { opAdminResultsAppeal, opAdminResultsAppealDetails } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  tableSearchContainer: {
    paddingBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  searchInput: {
    maxWidth: '100%',
  },
  feeCell: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const useRawFilters = () => {
  const candidateType = useAPIDropdown(
    () => candidateService.appeal.opadmin.useGetCandidateTypes(),
    true,
    dropDownMappingToSelect
  );

  const examLevel = useAPIDropdown(
    () => candidateService.appeal.opadmin.useGetNationalExamLevel(),
    true,
    dropDownMappingToSelect
  );

  const paymentMode = useAPIDropdown(
    () => candidateService.resit.opadmin.useGetPaymentModeOptions({}),
    true,
    dropDownMappingToSelect
  );

  return {
    candidateType,
    examLevel,
    paymentMode,
  };
};

const useTableColumns = (
  remoteTotalRowsSelected: number,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  filters: {
    filterMapping: {
      filterMappingCandidateType: any;
      filterMappingExamLevel: any;
      filterMappingPaymentMode: any;
    };
    filterCode: {
      filterCodeCandidateType: any;
      filterCodeExamLevel: any;
      filterCodePaymentMode: any;
    };
  },
  t: TFunction
): MUIDataTableColumn[] => {
  const { classes } = useStyles();

  const {
    filterMappingPaymentMode,
    filterMappingCandidateType,
    filterMappingExamLevel,
  } = filters.filterMapping;
  const {
    filterCodePaymentMode,
    filterCodeCandidateType,
    filterCodeExamLevel,
  } = filters.filterCode;

  const history = useHistory();

  const onViewDetails = (uuid: string) => {
    history.push(`/${admin}/${opAdminResultsAppeal.route}/${opAdminResultsAppealDetails.route}`, {
      uuid,
    });
  };

  return [
    {
      name: 'candidateId',
      label: 'NRIC/FIN/Passport No.',
      options: {
        searchable: false,
        sort: true,
        filter: false,
      },
    },
    {
      name: 'candidateName',
      label: 'Candidate Name',
      options: {
        searchable: false,
        sort: true,
        filter: false,
      },
    },
    candidateTypeColumn(
      classes,
      tableDispatch,
      filterMappingCandidateType,
      state,
      filterCodeCandidateType
    ),
    examLevelColumn(classes, tableDispatch, filterMappingExamLevel, state, filterCodeExamLevel),
    applicationDateColumn(),
    paymentModeColumn(
      classes,
      tableDispatch,
      filterMappingPaymentMode,
      state,
      filterCodePaymentMode
    ),
    appealFeeColumn(classes, state, {
      appealFeeLabel: t('appeal->>ResultsAppeal.Appeal Fee'),
      inclusiveGstLabel: t('appeal->>ResultsAppeal.(incl. of GST) (SGD)'),
    }),
    actionColumn(classes, onViewDetails, 'action', {
      label: 'Action',
      viewDetailsLabel: 'View Details',
    }),
    {
      name: 'uuid',
      options: {
        searchable: false,
        display: false,
        filter: false,
      },
    },
  ];
};

const useExportDownloadAction = (
  data: AxiosResponse<PaginatedData<ResultsAppealSearchResponse>> | undefined
) => {
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogExportState, setDialogExportState] = useState<boolean>(false);

  const [fileUUID, setFileUUID] = useState('');

  const { mutate: exportMutate } = candidateService.appeal.opadmin.usePostResultsAppealExportExcel(
    (response) => {
      setFileUUID(response.data.uuid);
    },
    (error) => null
  );

  /** to define bulk download
   * */
  const onHandleExport = (inputRequest: ResultsAppealFunctionRequest) => {
    setDialogExportState(true);
    exportMutate(inputRequest);
  };

  /** handle download action
   * */
  const downloadCSV = (downloadResponse: RetrieveS3URLResponse) => {
    setDialogExportState(true);
    if (downloadResponse.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogExportState(false);
    }
    setFileDownloadStatus(downloadResponse.fileStatus as FileUploadState);
  };

  /** handle dialog close
   * */
  const onDialogExportClose = () => {
    setDialogExportState(false);
    setFileUUID('');
  };

  return {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton: dialogExportState || data === undefined || data.data.totalElements === 0,
  };
};

const useBulkActions = (
  state: TableState,
  translations: {
    exportSelectedLabel: string;
  },
  exportAction: {
    onHandleExport: (input: ResultsAppealFunctionRequest) => void;
    fileUUID: string;
    downloadCSV: (status: RetrieveS3URLResponse) => void;
    disableExportButton: boolean;
  }
) =>
  useMemo(
    () => [
      <DownloadFromS3Action
        color="primary"
        actionType="link"
        requestRoute={{ data: exportAction.fileUUID, url: '/api/s3/files' }}
        disable={exportAction.disableExportButton}
        key="download-s3-action"
        onClick={() => {
          const selectedList: string[] = Object.keys(state.rowsSelected).map(
            (s) => state.rowsSelected[s].uuid
          );
          exportAction.onHandleExport({
            uuid: selectedList,
            candidateType: getIndexValue(state.filters.candidateType, 0),
            examLevel: getIndexValue(state.filters.examLevel, 0),
            paymentModes: state.filters.paymentMode || [],
            q: state.search ?? '',
          });
        }}
        onDownload={(status) => exportAction.downloadCSV(status)}
        label={translations.exportSelectedLabel}
      />,
    ],
    [
      exportAction,
      state.rowsSelected,
      state.filters.candidateType,
      state.filters.paymentMode,
      state.filters.examLevel,
      state.search,
      translations.exportSelectedLabel,
    ]
  );

const formSearchQuery = (state: TableState, csrfToken: string) => {
  let path = formAPIPageable({
    order: state.order,
    sort: state.sortColumn,
    page: state.page,
    pageSize: state.size,
  });
  if (path !== '?') {
    path += '&';
  }

  if (getIndexValue(state.filters.examLevel, 0)) {
    path += `examLevel=${getIndexValue(state.filters.examLevel, 0)}&`;
  }

  if (getIndexValue(state.filters.candidateType, 0)) {
    path += `candidateType=${getIndexValue(state.filters.candidateType, 0)}&`;
  }

  const paymentModes = (state.filters.paymentMode as Array<string>) || [];
  if (paymentModes.length) {
    path += `paymentModes=[${state.filters.paymentMode.join()}]&`;
  }

  if (state.search) {
    const searchField = encodeURIComponent(encryptData(state.search, csrfToken));
    path += `q=${searchField}&`;
  }

  if (path.length) {
    path = path.substring(0, path.length - 1);
  }

  return path;
};

const useSearchAndFilter = (
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  csrfToken: string
) => {
  const history = useHistory();
  const location = useLocation();

  const {
    _refetch,
    isLoading,
    data,
  } = candidateService.appeal.opadmin.usePostRawResultsAppealSearch(
    {
      order: state.order,
      sort: state.sortColumn,
      page: state.page,
      pageSize: state.size,
    },
    {
      candidateType: getIndexValue(state.filters.candidateType, 0),
      paymentModes: state.filters.paymentMode || [],
      examLevel: getIndexValue(state.filters.examLevel, 0),
      q: state.search ?? '',
    },
    {
      enabled: false,
    }
  );

  useEffect(() => {
    if (data) {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.data.content,
          count: data.data.totalElements,
        },
      } as TableActionTypes);
      history.replace(`${location.pathname}${formSearchQuery(state, csrfToken)}`);
    }
  }, [data, tableDispatch, history, state, location.pathname, csrfToken]);

  const {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  } = useExportDownloadAction(data);

  return {
    _refetch,
    isLoading,
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  };
};

const getFiltersMap = (
  examLevel: string | undefined,
  candidateType: string | undefined,
  paymentModes: string | undefined
) => {
  const filterMap: {
    [key: string]: any;
  } = {};
  if (examLevel) {
    filterMap.examLevel = [examLevel];
  }

  if (candidateType) {
    filterMap.candidateType = [candidateType];
  }

  if (paymentModes) {
    filterMap.paymentMode = paymentModes.replace('[', '').replace(']', '').split(',');
  }

  return filterMap;
};

const useGetRequestParam = (csrfToken: string | undefined) => {
  const location = useLocation<ResultsAppealRequestParam>();
  const requestParam = parseLocationSearch(location.search);
  const filterMap = getFiltersMap(
    requestParam.examLevel,
    requestParam.candidateType,
    requestParam.paymentModes
  );
  const sortColumn = requestParam.sort?.split(',')[0];
  const order = requestParam.sort?.split(',')[1] === 'asc' ? 'asc' : 'desc';
  let decryptQ = '';
  if (requestParam.q) {
    decryptQ = decryptData(decodeURIComponent(requestParam.q || ''), csrfToken);
  }
  return {
    filterMap,
    page: requestParam.page,
    size: requestParam.size,
    q: decryptQ,
    sort: sortColumn,
    order,
  };
};

const ResultsAppealList = () => {
  const { classes } = useStyles();

  useGetCheckAuthorityExist({ authorityName: AUTHORITY_NAMES.CP_ADMIN_APPEAL_REPORT_VIEW });
  const { t } = useTranslation();
  const { data: userDetailData } = useUserDetails();
  const csrfToken = userDetailData?.data?.csrfToken || '';
  const requestParam = useGetRequestParam(csrfToken);

  const { state, dispatch, tOptions, selectionToolbar, remoteTotalRowsSelected } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: requestParam.sort || 'applicationDate',
    order: requestParam.order === 'asc' ? 'asc' : 'desc',
    size: requestParam.size !== undefined ? parseInt(requestParam.size, 10) : 5,
    page: requestParam.page !== undefined ? parseInt(requestParam.page, 10) : 0,
    search: requestParam.q || '',
    filters: requestParam.filterMap,
  });

  const {
    _refetch,
    isLoading,
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  } = useSearchAndFilter(state, dispatch, csrfToken);

  useEffect(() => {
    _refetch();
  }, [
    _refetch,
    state.filters,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    state.search,
  ]);

  const bulkActionComponent = useBulkActions(
    state,
    {
      exportSelectedLabel: t('admin->>IExamCandidateCreate.Export Selected'),
    },
    {
      onHandleExport,
      fileUUID,
      downloadCSV,
      disableExportButton,
    }
  );

  const exportAllAction = useCallback(
    () => (
      <>
        <DownloadFromS3Action
          color="primary"
          actionType="link"
          requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
          disable={disableExportButton}
          onClick={() => {
            onHandleExport({
              uuid: [],
              candidateType: getIndexValue(state.filters.candidateType, 0),
              examLevel: getIndexValue(state.filters.examLevel, 0),
              paymentModes: state.filters.paymentMode || [],
              q: state.search ?? '',
            });
          }}
          onDownload={(status) => downloadCSV(status)}
          label={t('admin->>IExamCandidateCreate.Export All')}
        />
      </>
    ),
    [
      disableExportButton,
      downloadCSV,
      fileUUID,
      onHandleExport,
      state.filters.candidateType,
      state.filters.examLevel,
      state.filters.paymentMode,
      state.search,
      t,
    ]
  );

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      pagination: true,
      filter: true,
      sort: true,
      filterType: 'multiselect',
      // need to put to make selection replacement work
      selectToolbarPlacement: 'replace',
      customToolbar: exportAllAction,
      sortOrder: {
        name: requestParam.sort || 'applicationDate',
        direction: requestParam.order === 'asc' ? 'asc' : 'desc',
      },
    }),
    [tOptions, exportAllAction, requestParam.sort, requestParam.order]
  );

  const {
    candidateType: {
      functionCode: filterCodeCandidateType,
      typeMapping: filterMappingCandidateType,
    },
    examLevel: { functionCode: filterCodeExamLevel, typeMapping: filterMappingExamLevel },
    paymentMode: { functionCode: filterCodePaymentMode, typeMapping: filterMappingPaymentMode },
  } = useRawFilters();

  const tableColumns = useTableColumns(
    remoteTotalRowsSelected,
    // @ts-ignore
    state,
    dispatch,
    {
      filterMapping: {
        filterMappingCandidateType,
        filterMappingExamLevel,
        filterMappingPaymentMode,
      },
      filterCode: {
        filterCodeCandidateType,
        filterCodeExamLevel,
        filterCodePaymentMode,
      },
    },
    t
  );

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('appeal->>ResultsAppeal.Results Appeal Applications')} />
      <Grid xs={12}>
        <Box className={classes.tableSearchContainer}>
          <SearchBar
            name="searchInput"
            className={classes.searchInput}
            ariaLabel="searchInput"
            defaultValue={requestParam.q}
            placeholder={t('appeal->>ResultsAppeal.Search by Name/NRIC/FIN/Passport No.')}
            onSearch={(str) => {
              dispatch({
                type: 'UPDATE_SEARCH',
                payload: str,
              });
            }}
          />
        </Box>
      </Grid>
      <MUIDataTable
        isLoading={isLoading}
        // @ts-ignore
        state={state}
        dispatch={dispatch}
        columns={tableColumns}
        // @ts-ignore
        data={state.data}
        options={customOptions}
        rowsPerPageOptions={[5, 10, 20, 50]}
        components={{ TableToolbarSelect: () => selectionToolbar(bulkActionComponent) }}
      />

      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogExportState}
        onDialogClose={onDialogExportClose}
      />
    </ContentTemplate>
  );
};

export default ResultsAppealList;
